import React, { useState } from 'react';

import { BookingInquiryOwnerChooseDialog } from '@components/admin/booking/BookingInquiryOwnerChooseDialog';
import { Layout } from '@components/admin/Layout';
import { Button } from '@components/Button';
import { BookingCreateForm } from '@components/utilities/BookingInquiry/BookingCreateForm';
import { VerticalSpace } from '@components/VerticalSpace';
import { Country } from '@models/Country';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: '予約作成',
    pleaseSelectOwner: 'Ownerを指定してください',
    selectFromMembers: '会員一覧から選択する',
    startCreatingBookingInquiry: '予約作成を始める',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    ...common.flexCenter,
    flex: 1,
  },
  buttonLabel: {
    ...common.normalText,
    marginRight: '1rem',
  },
  buttonText: {
    ...common.smallerText,
    ...common.baseButton,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    borderWidth: 1.5,
    borderRadius: 52.95,
    padding: '0.25rem 1rem',
  },
  button: {
    width: '15rem',
    height: '2.25rem !important',
    borderRadius: '0.25rem',
  },
  editButton: {
    ...common.link,
    marginLeft: '9px',
  },
  row: {
    flexGrow: 0,
  },
}));

export interface BookingCreateCompanyInfo {
  id: number | undefined | null;
  companyName: string | undefined | null;
  memberNo: string | undefined | null;
  country: Country;
}

export const BookingCreate: React.FC = () => {
  const classes = { ...useStyles(), ...useCommonStyles() };
  const [ownerId, setOwnerId] = useState(0);
  const [companyInfo, setCompanyInfo] = useState<BookingCreateCompanyInfo>();
  const [ownerName, setOwnerName] = useState('-');
  const [creating, setCreating] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const renderOwner = () => (
    <>
      <span className={clsx(classes.textPrimary, classes.fontWeight700)}>
        {companyInfo?.companyName || '-'}
      </span>
      <span>{` (${companyInfo?.memberNo || '-'})`}</span>
      <span className={classes.fontWeight700}>{`-${ownerName}`}</span>
    </>
  );

  return (
    <>
      <BookingInquiryOwnerChooseDialog
        onClose={() => setOpenDialog(false)}
        open={openDialog}
        setOwnerId={setOwnerId}
        setCompanyInfo={setCompanyInfo}
        setOwnerName={setOwnerName}
      />
      {!creating && (
        <Layout title={strings.title}>
          <div className={classes.container}>
            <div className={clsx(classes.row, classes.biggerText, classes.fontWeight700)}>
              {strings.pleaseSelectOwner}
            </div>
            <VerticalSpace height={30} />
            <div className={clsx(classes.row, classes.flexCenter)}>
              <span className={classes.buttonLabel}>Owner:</span>
              {!!ownerId && renderOwner()}
              {!!ownerId && (
                <span className={classes.editButton} onClick={() => setOpenDialog(true)}>
                  {formStrings.change}
                </span>
              )}
              {!ownerId && (
                <span className={classes.buttonText} onClick={() => setOpenDialog(true)}>
                  {strings.selectFromMembers}
                </span>
              )}
            </div>
            <VerticalSpace height={30} />
            <div className={clsx(classes.row, classes.flexCenter)}>
              <Button
                data-testid="start-creating-button"
                className={classes.button}
                text={strings.startCreatingBookingInquiry}
                color={'primary'}
                size={'default'}
                onClick={() => setCreating(true)}
                disabled={!ownerId}
              />
            </div>
          </div>
        </Layout>
      )}

      {creating && (
        <BookingCreateForm
          isDashboard
          ownerId={ownerId}
          ownerCompanyId={companyInfo?.id!}
          ownerCompanyCountry={companyInfo?.country || 'jp'}
          onChangeCreating={setCreating}
        />
      )}
    </>
  );
};
